import { FormEvent, useCallback } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { addToCartEvent } from '@portal/analytics/events/buildBundle';
import { SHOPIFY_STORE_BRAND } from '@portal/config/base';
import { StoreEnum } from '@portal/schema/storeCode';
import { LocaleEnum, LocaleStore } from '@portal/schema/locale';
import { useLocale } from '@portal/shared/hooks/useLocale';
import { useShopifyCartUrl } from '@portal/shared/hooks/useShopifyCartUrl';
import { useBuyerIdentityCountryCode } from '@portal/state/hooks/useBuyerIdentityCountryCode';
import { isStandaloneProduct } from '@portal/shared/utils/shopify/isStandaloneProduct';
import { selectProductEvent } from 'analytics/events/studio';
import { createShopifyCart } from 'api/shopifyV2/cart';
import type { CartLine } from 'api/shopifyV2/types';
import { MONITORING_TAGS } from 'constants/monitoring';
import { LATEST_CHECKOUT_EXPERIENCE } from 'constants/sessionStorage';
import { useAtom } from 'jotai';
import cookies from 'js-cookie';
import { useRouter } from 'next/router';
import { isMobileTablet } from 'shared/utils/devices';
import { safeLocalStorage } from 'shared/utils/safeLocalStorage';
import { getFormattedShopifyId, getIdFromShopifyFormattedId } from 'shared/utils/shopify/ids';
import { useSetCurrentBabProductAtom } from 'state/atoms';
import { CHECKOUT_TYPE } from 'state/cart/constants';
import { useStoreValue } from 'state/hooks/useStore';
import { useStoreCurrency } from 'state/hooks/useStoreCurrency';
import { getTrackingParams } from 'state/localStorage/trackingParams';
import { Steps } from '../BuildBundle.enum';
import {
  checkoutStatusAtom,
  firstStepOptionAtom,
  landerUrl,
  secondStepOptionAtom,
  secondStepVariantAtom,
  stepAtom
} from '../atoms';
import { ProductTypeOption, Step } from '../constants';
import type { BuildShopifyCartUrlParams, ShopifyCartItem } from '../types';
import { addWelcomeKit } from '../utils';
import { useBabSanity } from './context';
import { useSessionValue } from '@portal/appLayer/auth';

export const useHandleSubmit = () => {
  const router = useRouter();
  const isMobileTabletDevice = isMobileTablet();
  const [firstStepOption] = useAtom(firstStepOptionAtom);
  const [secondStepVariant] = useAtom(secondStepVariantAtom);
  const [landerUrlParam] = useAtom(landerUrl);
  const [step] = useAtom(stepAtom);
  const [secondStepOption] = useAtom(secondStepOptionAtom);
  const [, setCheckoutStatus] = useAtom(checkoutStatusAtom);
  const { usedProductsMap, steps } = useBabSanity();
  const trackingParams = getTrackingParams();
  const setCurrentBabProduct = useSetCurrentBabProductAtom();
  const store = useStoreValue();
  const { currency } = useStoreCurrency();
  const locale = useLocale();
  const { getShopifyCartUrl } = useShopifyCartUrl();
  const buyerIdentityCountryCode = useBuyerIdentityCountryCode();
  const session = useSessionValue();

  const pageSlug = router.asPath;
  const utmString = Object.keys(trackingParams)
    .filter((key) => key.startsWith('utm_'))
    .map((key) => `${key}=${trackingParams[key]}`)
    .join('&');

  const fbuyObject =
    trackingParams.referralCode || trackingParams.fbshare
      ? JSON.stringify({
          ...(trackingParams.referralCode && { referralCode: trackingParams.referralCode }),
          ...(trackingParams.fbshare && { fbshare: trackingParams.fbshare })
        })
      : null;

  // 6H Analytics click ID
  const cid = trackingParams.sub2;

  const handleStep1Next = useCallback(() => {
    const slug = router.query.slug || '';

    if (firstStepOption && secondStepVariant) {
      // insert &product=travel-packs into query param if it is selected
      router.push(
        {
          pathname: `/${slug[0]}`,
          query: {
            step: Steps.SECOND_STEP.toString(),
            ...(firstStepOption === ProductTypeOption.SECOND_PRODUCT && { product: 'travel-packs' })
          }
        },
        undefined,
        { shallow: true }
      );

      selectProductEvent({
        store,
        clickText: steps.firstButtonLink.portableText
          ? steps.firstButtonLink.portableText[0].children[0].text
          : undefined,
        itemId: firstStepOption,
        itemName: secondStepVariant.name,
        location: router.asPath,
        isStandaloneProduct: isStandaloneProduct(firstStepOption, store)
      });
    }
  }, [router, firstStepOption, secondStepVariant, steps.firstButtonLink.portableText, store]);

  const handleCreateCheckout = useCallback(async () => {
    setCheckoutStatus('loading');
    if (!secondStepOption.product) return;

    const promoCode = cookies.get('promo_code');
    const variantIds = cookies.get('variant_id') ? cookies.get('variant_id')?.split(',') : [];

    const friendbuyState = safeLocalStorage.getItem('persist:friendbuy-msdk-06192019-root') || '{}';

    setCurrentBabProduct({
      imageMobile: secondStepOption.product.imageMobile,
      description: secondStepOption.product.description,
      productTitle: secondStepOption.product.productTitle
    });
    // secondStepOption.product
    try {
      const productVariantId = secondStepOption.product.ProductIds.find(
        ({ targetStore }: { targetStore: string }) => targetStore === store
      );

      if (!productVariantId) throw Error('Product ID not found');
      const variantId = productVariantId?.productVariantId as string;
      const productData = usedProductsMap?.[getFormattedShopifyId('ProductVariant', variantId)];

      if (!productData) {
        // eslint-disable-next-line no-console
        console.log(
          `Product not found: variantID ${getFormattedShopifyId('ProductVariant', variantId)}`
        );
      }

      const getGiftProducts = () => {
        const { product } = secondStepOption;

        if (product?.gifts && product.gifts.length) {
          return product.gifts
            .filter((gift) => gift.targetStore === store)
            .filter((gift) => gift.amount > 0)
            .map((gift) => {
              const giftId = gift.variantId;
              return {
                merchandiseId: getFormattedShopifyId('ProductVariant', giftId),
                quantity: gift.amount
              } as ShopifyCartItem;
            });
        }

        const giftProducts = secondStepOption.product?.giftProducts || [];

        if (!Array.isArray(giftProducts) || giftProducts.length === 0) {
          return [];
        }

        return giftProducts
          ?.filter((gift) => gift.targetStore === store)
          ?.map(
            (giftProductVariantId) =>
              ({
                merchandiseId: getFormattedShopifyId(
                  'ProductVariant',
                  giftProductVariantId.variantId
                ),
                quantity: 1
              }) as ShopifyCartItem
          );
      };

      const cartInput: BuildShopifyCartUrlParams = {
        lines: [
          ...addWelcomeKit(secondStepOption.product, store),
          ...getGiftProducts(),
          {
            merchandiseId: getFormattedShopifyId('ProductVariant', variantId),
            quantity: secondStepOption.quantity,
            // conditionally add sellingPlanId
            ...(productVariantId.sellingPlanId && parseInt(productVariantId.sellingPlanId, 10) > 0
              ? {
                  sellingPlanId: getFormattedShopifyId(
                    'SellingPlan',
                    productVariantId.sellingPlanId
                  )
                }
              : {}),
            ...(productVariantId.sellingPlanId && parseInt(productVariantId.sellingPlanId, 10) > 0
              ? {
                  attributes: [
                    {
                      key: '__shop_subscriptions',
                      value: 'true'
                    }
                  ]
                }
              : {})
          }
        ]
      };

      if (promoCode) cartInput.discountCodes = [promoCode];

      // Geolocate for APAC and EU only
      if (locale === LocaleEnum.apacUS || LocaleStore[locale] === StoreEnum.eu) {
        cartInput.buyerIdentity = {
          countryCode: buyerIdentityCountryCode
        };
      }

      if (variantIds)
        cartInput.lines.push(
          ...variantIds.map((id: string) => ({
            merchandiseId: getFormattedShopifyId('ProductVariant', id),
            quantity: 1
          }))
        );

      cartInput.attributes = [
        { key: 'checkoutOriginUrl', value: window.location.href },
        { key: CHECKOUT_TYPE.key, value: CHECKOUT_TYPE.BAB }
      ];

      sessionStorage.setItem(LATEST_CHECKOUT_EXPERIENCE.key, LATEST_CHECKOUT_EXPERIENCE.BAB);

      if (landerUrlParam) {
        cartInput.attributes.push({
          key: 'lander-url',
          value: landerUrlParam
        });
      }

      if (utmString) {
        cartInput.attributes.push({
          key: 'utm-data',
          value: utmString
        });
      }

      if (fbuyObject) {
        cartInput.attributes.push({
          key: 'fbuy',
          value: fbuyObject
        });
      }

      if (friendbuyState && Object.keys(JSON.parse(friendbuyState)).length > 0) {
        cartInput.attributes.push({
          key: 'fbuyObject',
          value: friendbuyState
        });
      }

      if (cid) {
        cartInput.attributes.push({
          key: '6hcid',
          value: cid
        });
      }

      const socialLadderTrackCode = cookies.get('sl-track-purchase');
      if (socialLadderTrackCode) {
        cartInput.attributes.push({
          key: 'social_ladder.tracking_cookie',
          value: socialLadderTrackCode
        });
      }

      const cart = await createShopifyCart(store, cartInput);
      const { cost, lines } = cart;
      const mappedLines = lines.map((line: CartLine) => {
        const itemId = getIdFromShopifyFormattedId(line.merchandise.id);

        return {
          item_id: itemId,
          item_name: line.merchandise.product.title,
          item_brand: SHOPIFY_STORE_BRAND[store],
          item_variant: line.merchandise.title,
          variant_id: itemId,
          price: line.merchandise.priceV2.amount,
          currency,
          quantity: line.quantity
        };
      });

      addToCartEvent({
        item_list_name: secondStepOption.product.productTitle,
        item_list_id: variantId,
        affiliation: utmString || pageSlug,
        items: mappedLines,
        eventId: cart.id,
        store,
        currency,
        value: cost.subtotalAmount.amount,
        deviceType: isMobileTabletDevice ? 'mobile' : 'desktop',
        isStandaloneProduct:
          lines.length === 1 && isStandaloneProduct(lines[0].merchandise.id, store),
        user: {
          email: session?.customer.email || ''
        }
      });

      if (cart?.id) {
        const cartUrl = await getShopifyCartUrl(store, cart.id, locale);
        // add cartId to local storage
        safeLocalStorage.setItem('cartId', cart.id);
        router.push(cartUrl);
      }
    } catch (err: unknown) {
      const tags = {
        [MONITORING_TAGS.MARKETING]: true,
        [MONITORING_TAGS.BUILD_A_BUNDLE]: true
      };
      datadogRum.addError(err, { tags });
    } finally {
      setCheckoutStatus('idle');
    }
  }, [
    secondStepOption,
    setCurrentBabProduct,
    store,
    usedProductsMap,
    landerUrlParam,
    utmString,
    fbuyObject,
    cid,
    pageSlug,
    currency,
    locale,
    router,
    isMobileTabletDevice,
    getShopifyCartUrl,
    setCheckoutStatus,
    buyerIdentityCountryCode,
    session?.customer.email
  ]);

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!secondStepVariant) return;
      if (step === Step.FIRST_STEP) {
        handleStep1Next();
      } else if (step === Step.SECOND_STEP) {
        handleCreateCheckout();
      }
    },
    [secondStepVariant, step, handleStep1Next, handleCreateCheckout]
  );

  return { handleSubmit };
};
